import { useState } from 'react';
import { Button } from '../../../components/form/button';
import { Modal } from '../../../components/modal/modal';
import { Spinner } from '../../../components/spinner/spinner';
import { DateTimePicker } from '../../../components/date-picker/date-time-picker';
import { DateTime } from 'luxon';
import { Typography } from '../../../components/typography/typography';
import { Order } from '../../../clients/quipli-service/order/order';
import { OrderInputItem, OrderInputRental } from '../../../clients/quipli-service/order/order-calculation';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import { toast } from '../../../util/toasts';

export const RentalDatesModal = ({
  order,
  orderInputs,
  timezone,
  onUpdate,
  onClose,
}: {
  order: Order;
  orderInputs: OrderInputItem[];
  timezone: string;
  onUpdate: () => void;
  onClose: () => void;
}) => {
  const { updateRentalDates } = useOrderApiV1();

  const rentalInputs = orderInputs.filter((input) => input.type === 'Rental');
  const rentalStartDates = rentalInputs.map((input) => (input as OrderInputRental).start);
  const rentalEndDates = rentalInputs.map((input) => (input as OrderInputRental).end);
  const minRentalStartDate = DateTime.min(...rentalStartDates);
  const maxRentalEndDate = DateTime.max(...(rentalEndDates.filter((d) => !!d) as DateTime[]));

  const [rentalStartDate, setRentalStartDate] = useState<DateTime | undefined>(minRentalStartDate);
  const [rentalEndDate, setRentalEndDate] = useState<DateTime | undefined>(maxRentalEndDate);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dateHasError, setDateHasError] = useState<boolean>(false);

  const handleClose = () => {
    if (isLoading) return;

    onClose();
  };

  const handleSubmit = async () => {
    if (isLoading) return;

    if (rentalEndDate && rentalStartDate && rentalEndDate < rentalStartDate) {
      setDateHasError(true);
      return;
    } else {
      setDateHasError(false);
    }

    setIsLoading(true);

    const updatedOrderInputs: OrderInputItem[] = [];
    orderInputs.forEach((input) => {
      if (input.type === 'Rental') {
        updatedOrderInputs.push({
          ...input,
          start: rentalStartDate!,
          end: rentalEndDate,
        });
      } else {
        updatedOrderInputs.push(input);
      }
    });

    try {
      await updateRentalDates(order.id, rentalStartDate!, rentalEndDate!);
      onUpdate();
    } catch (e: any) {
      toast('There was an error updating the rental dates', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title="Adjust Rental Dates"
      className="!bg-grey-25"
      isOpen={true}
      close={handleClose}
      actions={
        isLoading ? (
          <Spinner />
        ) : (
          <>
            <Button className="button-secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button className="button-yellow" onClick={handleSubmit} disabled={dateHasError}>
              Update
            </Button>
          </>
        )
      }
    >
      <div>
        <div className="text-center px-4 mb-4">
          <Typography type="text-sm" className="text-grey-500">
            All rental items will be updated to the new rental dates.
          </Typography>
        </div>
      </div>

      {/* Rental Start */}
      <div>
        <DateTimePicker
          label="Rental Start"
          isRequired
          value={rentalStartDate}
          onChange={setRentalStartDate}
          timezone={timezone}
        />
      </div>

      {/* Rental End */}
      <div className="mb-4">
        <DateTimePicker
          label="Rental End"
          isRequired
          value={rentalEndDate}
          onChange={setRentalEndDate}
          timezone={timezone}
        />
        {dateHasError && <div className="text-red-600 pt-2 text-sm">Date must be greater than "Rental Start"</div>}
      </div>
    </Modal>
  );
};
